import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, CheckCircle, XCircle, Flag, RefreshCw, Trash2 } from 'lucide-react';

interface FlaggedActivity {
  id: string;
  latitude: number;
  longitude: number;
  city?: string;
  country?: string;
  place_name?: string;
  activities: string[];
  emoji?: string;
  created_at: string;
  flag_count: number;
  flag_reasons?: Array<{
    flag_type: string;
    count: number;
  }>;
}

export default function ModerationDashboard() {
  const [flaggedActivities, setFlaggedActivities] = useState<FlaggedActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Simple password protection for moderation
  const handleLogin = () => {
    // In production, this should be a proper auth system
    if (password === 'admin123') {
      setIsAuthenticated(true);
      localStorage.setItem('mod_auth', 'true');
    } else {
      alert('Incorrect password');
    }
  };

  useEffect(() => {
    // Check if already authenticated
    if (localStorage.getItem('mod_auth') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const fetchFlaggedActivities = async () => {
    try {
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/global/flagged');
      const data = await response.json();
      if (data.success) {
        setFlaggedActivities(data.activities);
      }
    } catch (error) {
      console.error('Failed to fetch flagged activities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchFlaggedActivities();
    }
  }, [isAuthenticated]);

  const handleApprove = async (activityId: string) => {
    try {
      const response = await fetch(`https://biig-api.b-9f2.workers.dev/api/global/moderate/${activityId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'approve' })
      });

      if (response.ok) {
        // Remove from list
        setFlaggedActivities(prev => prev.filter(a => a.id !== activityId));
        alert('Activity approved and flags cleared');
      }
    } catch (error) {
      console.error('Failed to approve activity:', error);
      alert('Failed to approve activity');
    }
  };

  const handleRemove = async (activityId: string) => {
    if (!confirm('Are you sure you want to remove this activity?')) return;

    try {
      const response = await fetch(`https://biig-api.b-9f2.workers.dev/api/global/moderate/${activityId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'remove' })
      });

      if (response.ok) {
        // Remove from list
        setFlaggedActivities(prev => prev.filter(a => a.id !== activityId));
        alert('Activity removed');
      }
    } catch (error) {
      console.error('Failed to remove activity:', error);
      alert('Failed to remove activity');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-6 h-6 text-indigo-600" />
            <h1 className="text-xl font-bold">Moderation Dashboard</h1>
          </div>
          <p className="text-gray-600 mb-4">Enter password to access moderation tools</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
            placeholder="Password"
            className="w-full px-4 py-2 border rounded-lg mb-4"
          />
          <button
            onClick={handleLogin}
            className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-indigo-600" />
              <div>
                <h1 className="text-2xl font-bold">Moderation Dashboard</h1>
                <p className="text-gray-600">Review and manage flagged content</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={fetchFlaggedActivities}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <Link
                to="/global"
                className="flex items-center gap-2 px-4 py-2 text-indigo-600 hover:text-indigo-700"
              >
                View Global Map →
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-700">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-semibold">Pending Review</span>
              </div>
              <div className="text-2xl font-bold text-yellow-900 mt-2">
                {flaggedActivities.length}
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-red-700">
                <Flag className="w-5 h-5" />
                <span className="font-semibold">High Priority (3+ flags)</span>
              </div>
              <div className="text-2xl font-bold text-red-900 mt-2">
                {flaggedActivities.filter(a => a.flag_count >= 3).length}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 text-gray-700">
                <Shield className="w-5 h-5" />
                <span className="font-semibold">Total Flags</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 mt-2">
                {flaggedActivities.reduce((sum, a) => sum + a.flag_count, 0)}
              </div>
            </div>
          </div>
        </div>

        {/* Flagged Activities */}
        {isLoading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Loading flagged activities...</p>
          </div>
        ) : flaggedActivities.length === 0 ? (
          <div className="bg-white rounded-lg shadow-lg p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">All Clear!</h2>
            <p className="text-gray-600">No flagged activities to review</p>
          </div>
        ) : (
          <div className="space-y-4">
            {flaggedActivities.map((activity) => (
              <div key={activity.id} className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{activity.emoji || '📍'}</span>
                    <div>
                      <h3 className="font-semibold text-lg">
                        {activity.city || 'Unknown'}, {activity.country || 'Unknown'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {new Date(activity.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      activity.flag_count >= 3 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      <Flag className="w-3 h-3 inline mr-1" />
                      {activity.flag_count} flags
                    </div>
                  </div>
                </div>

                {/* Activities */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Reported Activities:</h4>
                  <ul className="space-y-1">
                    {activity.activities.map((act, idx) => (
                      <li key={idx} className="text-sm text-gray-600">
                        • {act}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Flag Reasons */}
                {activity.flag_reasons && activity.flag_reasons.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-sm text-gray-700 mb-2">Flag Types:</h4>
                    <div className="flex gap-2 flex-wrap">
                      {activity.flag_reasons.map((reason, idx) => {
                        const flagColors = {
                          duplicate: 'bg-blue-100 text-blue-700',
                          inappropriate: 'bg-orange-100 text-orange-700',
                          spam: 'bg-red-100 text-red-700',
                          other: 'bg-gray-100 text-gray-700'
                        };
                        const flagEmojis = {
                          duplicate: '♊',
                          inappropriate: '⚠️',
                          spam: '🚫',
                          other: '🚩'
                        };
                        const color = flagColors[reason.flag_type as keyof typeof flagColors] || flagColors.other;
                        const emoji = flagEmojis[reason.flag_type as keyof typeof flagEmojis] || flagEmojis.other;
                        
                        return (
                          <span key={idx} className={`px-3 py-1 ${color} text-sm rounded-full font-medium flex items-center gap-1`}>
                            <span>{emoji}</span>
                            <span className="capitalize">{reason.flag_type}</span>
                            <span className="font-bold">({reason.count})</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleRemove(activity.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove Activity
                  </button>
                  <button
                    onClick={() => handleApprove(activity.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Dismiss Flags (Keep Activity)
                  </button>
                  <a
                    href={`https://www.google.com/maps?q=${activity.latitude},${activity.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    View on Map →
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}